import {
  AGE_CHINESE_EN,
  AGE_CHINESE_ZH,
  AGE_ENGLISH_EN,
  AGE_ENGLISH_ZH,
  AGE_MATH_EN,
  AGE_MATH_ZH,
} from '../components/landing/constants';
import {
  AGE_CHINESE_ZH as AGE_CHINESE_ZH_CHOOSE,
  AGE_CHINESE_EN as AGE_CHINESE_EN_CHOOSE,
  AGE_MATH_SERVICE_ZH,
  AGE_MATH_SERVICE_EN,
  AGE_ENGLISH_SERVICE_ZH,
  AGE_ENGLISH_SERVICE_EN,
  MATH_ZH_COUNTRY_STUDENT_GRADE,
  MATH_EN_COUNTRY_STUDENT_GRADE,
  ENGLISH_CH_COUNTRY_STUDENT_GRADE,
  ENGLISH_EN_COUNTRY_STUDENT_GRADE,
} from './config';
import imgIp5Math from '/public/images/loading/ip-5-math.png';
import logoMath from '/public/images/loading/logo-math.svg';
import imgIp5 from '/public/images/loading/ip-5.png';
import imgLogo from '/public/images/loading/logo.svg';
import logoEnglish from '/public/images/loading/logo-english.svg';

interface FixContent {
  leftImg: any;
  centerImg: string;
  centerText: string;
}

interface PCFixedContent {
  [key: string]: FixContent;
}

const optionRules = [
  {
    match: (subject: string, language: string) => subject === 'MATH' && language === 'EN',
    action: (isChoose?: boolean) => (isChoose ? AGE_MATH_SERVICE_EN : AGE_MATH_EN),
  },
  {
    match: (subject: string, language: string) => subject === 'MATH' && language === 'CH',
    action: (isChoose?: boolean) => (isChoose ? AGE_MATH_SERVICE_ZH : AGE_MATH_ZH),
  },
  {
    match: (subject: string, language: string) => subject === 'CHINESE' && language === 'EN',
    action: (isChoose?: boolean) => (isChoose ? AGE_CHINESE_EN_CHOOSE : AGE_CHINESE_EN),
  },
  {
    match: (subject: string, language: string) => subject === 'CHINESE' && language === 'CH',
    action: (isChoose?: boolean) => (isChoose ? AGE_CHINESE_ZH_CHOOSE : AGE_CHINESE_ZH),
  },
  {
    match: (subject: string, language: string) => subject === 'ENGLISH' && language === 'EN',
    action: (isChoose?: boolean) => (isChoose ? AGE_ENGLISH_SERVICE_EN : AGE_ENGLISH_EN),
  },
  {
    match: (subject: string, language: string) => subject === 'ENGLISH' && language === 'CH',
    action: (isChoose?: boolean) => (isChoose ? AGE_ENGLISH_SERVICE_ZH : AGE_ENGLISH_ZH),
  },
];

/**
 * 获取年龄选项
 *
 * @param {string} subject 学科
 * @param {string} language 页面语言
 * @param {boolean} isChoose 是否是约课页
 * @return {array}
 */
export const getAgeOptions = (subject: string, language: string, isChoose?: boolean) => {
  const result = optionRules.find((item) => item.match(subject, language));

  return result?.action(isChoose);
};

export const getGradeOptionsByCode = (language?: string, subject?: string, code?: string) => {
  // 根据地区码选择对应的数组
  let selectedArray: any =
    subject === 'MATH'
      ? language === 'CH'
        ? MATH_ZH_COUNTRY_STUDENT_GRADE
        : MATH_EN_COUNTRY_STUDENT_GRADE
      : language === 'CH'
      ? ENGLISH_CH_COUNTRY_STUDENT_GRADE
      : ENGLISH_EN_COUNTRY_STUDENT_GRADE;
  let selectOptions = selectedArray[code || '+1'] || selectedArray.other;
  return selectOptions;
};

export const getGradeOptions = (language: string, list: any) => {
  let arr: any = [];
  if (list && list.length > 0) {
    list.forEach((item: any) => {
      arr.push({
        label: language === 'CH' ? item.gradeZh : item.gradeEn,
        value: item.grade,
        age: item.age,
      });
    });
  }
  return arr;
};

// 根据年级映射年龄
export const getAgeValueByGrade = (language?: string, subject?: string, code?: string, grade?: string) => {
  const ageList = getGradeOptionsByCode(language, subject, code);
  const ageValue = ageList.find((item: any) => item.value === grade)?.age;
  return ageValue;
};
// 根据年级映射年级label
export const getGradeLabelByGrade = (language?: string, subject?: string, code?: string, grade?: string) => {
  const ageList = getGradeOptionsByCode(language, subject, code);
  const ageValue = ageList.find((item: any) => item.value === grade)?.label;
  return ageValue;
};

// 获取PC端底部固定数据
export const getPCFixed = (subject: string, langText: any) => {
  const content: PCFixedContent = {
    MATH: {
      leftImg: imgIp5Math,
      centerImg: logoMath,
      centerText: langText.mathCourseSlogan,
    },
    CHINESE: {
      leftImg: imgIp5,
      centerImg: imgLogo,
      centerText: langText.chineseCourseSlogan,
    },
    ENGLISH: {
      leftImg: imgIp5,
      centerImg: logoEnglish,
      centerText: langText.englishCourseSlogan,
    },
  };

  return content[subject || 'CHINESE'];
};

/**
 * 根据科目获取主题色
 *
 * @param {string} subject 学科
 * @return {*}
 */
export const getThemeColor = (subject: string) => {
  const themeMap = {
    CHINESE: { validColor: '#FF5353', disabledColor: '#FFCFCF' },
    MATH: { validColor: '#0B88FA', disabledColor: 'rgba(11, 136, 250, 0.2)' },
    ENGLISH: { validColor: '#6D38FB', disabledColor: 'rgba(109, 56, 251, 0.2)' },
  } as any;

  return themeMap[subject || 'CHINESE'];
};

/**
 * @param {string} booking
 * @returns {string}
 */
export const getBookingTrackingText = (booking?: boolean) => {
  return booking ? 'GBDLandingPage' : 'FunnelLandingPage';
};

/**
 * 虚拟约课根据科目，语言 确定科目名称
 *
 * @param {string} subject
 * @param {string} language
 * @returns {string}
 */

interface CourseData {
  [subject: string]: {
    [language: string]: string;
  };
}

const courseData: CourseData = {
  CHINESE: {
    zh: '中文1v1私教课',
    en: '1-on-1 Chinese Language class',
  },
  MATH: {
    zh: '数学名师直播课',
    en: 'Live interactive Math class',
  },
  ENGLISH: {
    zh: '英文名师直播课',
    en: 'Live English Language Arts class',
  },
};

export const getBookingCourseTitle = (subject: string, language: string): string | undefined => {
  if (courseData.hasOwnProperty(subject) && courseData[subject].hasOwnProperty(language)) {
    return courseData[subject][language];
  } else {
    return undefined;
  }
};
